/*
 * Shared store-search logic.
 *
 * Two things search for stores now — the header dropdown (store-selector.js)
 * and the full-page locator (store-locator.js) — and they must agree. Two
 * copies of a haversine and two copies of "which stores have usable
 * coordinates" is exactly the drift that snippets/store-pickup-data.liquid was
 * created to prevent on the pickup side. So the logic lives here once and both
 * call it.
 *
 * Plain global rather than a module: theme assets are loaded as classic
 * scripts, and a module would change the load order semantics for every
 * consumer. Load this before either consumer.
 *
 * The data files are fetched at most once per page, not once per component:
 * the promises are held at module scope, so a page carrying both the header
 * dropdown and the locator downloads the 325 KB postal table a single time.
 */
/* Guarded because a page can legitimately include this twice — the header
   dropdown loads it, and so does the locator section. Re-running the factory
   would hand out a fresh, empty promise cache and re-download the postal
   table under any search already in flight. */
window.TandyStoreSearch = window.TandyStoreSearch || (function () {
  'use strict';

  const scriptPromises = {};

  /* Both data files load on first search only, so visitors who never open the
     store finder pay nothing for them. */
  function loadScriptOnce(src, globalName) {
    if (window[globalName]) return Promise.resolve(true);
    if (scriptPromises[globalName]) return scriptPromises[globalName];
    if (!src) {
      console.error(`store-search: missing asset URL for ${globalName}.`);
      return Promise.resolve(false);
    }
    scriptPromises[globalName] = new Promise(resolve => {
      const tag = document.createElement('script');
      tag.src = src;
      tag.onload = () => resolve(Boolean(window[globalName]));
      tag.onerror = () => {
        console.error('store-search: failed to load', src);
        resolve(false);
      };
      document.head.appendChild(tag);
    });
    return scriptPromises[globalName];
  }

  function loadPostal(src) {
    return loadScriptOnce(src, 'TandyPostal');
  }

  /*
   * Store directory. Contains only locations carrying custom.storeid, which is
   * what keeps warehouses, dropship and HQ locations out of the finder.
   *
   * Two shapes, because this is mid-migration, and which one arrives is
   * decided in Liquid by snippets/store-directory-src.liquid:
   *
   *   *.js    the generated asset, which sets window.TandyStores. Carries
   *           DRAFT entries, which is the only reason it still exists.
   *   URL     /pages/store-directory, rendered live from the metaobject.
   *           Plain JSON, no build step, nothing to regenerate when a store
   *           opens.
   *
   * Sniffing the extension rather than taking a second argument keeps the
   * cutover to a one-line Liquid change with no JavaScript deploy behind it,
   * and keeps rollback equally cheap. When the asset goes, so does the branch.
   *
   * Resolves to [] rather than throwing, so the caller renders a message
   * instead of dying.
   */
  let directoryPromise = null;

  function loadDirectory(src) {
    // Held at module scope like the postal table: a page carrying both the
    // header dropdown and the locator must not fetch this twice. Every caller
    // passes the same snippet's output, so one promise is enough.
    if (!directoryPromise) directoryPromise = fetchDirectory(src);
    return directoryPromise;
  }

  async function fetchDirectory(src) {
    const stores = /\.js(\?|$)/.test(src || '')
      ? await loadDirectoryScript(src)
      : await loadDirectoryJson(src);

    if (!Array.isArray(stores)) {
      /* Named separately from "empty" because the two mean different things and
         one of them was a silent outage: an object here means the endpoint
         answered with something that is not the directory at all. */
      console.error('store-search: store directory was not an array, got', stores);
      return [];
    }
    if (!stores.length) {
      console.error('store-search: store directory unavailable, search cannot return results.');
      return [];
    }
    return stores;
  }

  async function loadDirectoryScript(src) {
    const ok = await loadScriptOnce(src, 'TandyStores');
    return ok ? window.TandyStores : null;
  }

  async function loadDirectoryJson(src) {
    if (!src) {
      console.error('store-search: missing store directory URL.');
      return null;
    }
    try {
      /* NO `Accept: application/json`, and that is not an oversight.
         The directory is a Shopify PAGE rendering a Liquid template. Ask a page
         URL for JSON and Shopify stops rendering the template and hands back its
         own representation of the page resource instead:

             {"page":{"id":124739911758,"body_html":"..."}}

         which parses perfectly and is not a store directory. That failed silently
         — the locator just reported "we couldn't look up store locations" — and
         it only appeared once the source flipped from the .js asset to the live
         endpoint, because the asset path never sent the header.

         Plain fetch gets the rendered template. */
      const response = await fetch(src);
      if (!response.ok) {
        console.error('store-search: store directory returned', response.status, src);
        return null;
      }
      // Served by a Liquid page template, so the content type is text/html and
      // response.json() would be within its rights to refuse it.
      return JSON.parse(await response.text());
    } catch (err) {
      console.error('store-search: could not read the store directory', err);
      return null;
    }
  }

  /* Local lookup — no geocoding API, no key, no per-search cost. Returns null
     rather than throwing. */
  async function lookupPostal(postal, country, sources) {
    const [ready] = await Promise.all([
      loadPostal(sources.postalSrc),
      loadDirectory(sources.directorySrc),
    ]);
    if (!ready) return null;
    return window.TandyPostal.lookup(postal, country);
  }

  function haversine(lat1, lon1, lat2, lon2) {
    const R = 6371e3;
    const toRad = deg => deg * Math.PI / 180;
    const φ1 = toRad(lat1);
    const φ2 = toRad(lat2);
    const Δφ = toRad(lat2 - lat1);
    const Δλ = toRad(lon2 - lon1);
    const a = Math.sin(Δφ / 2) ** 2 + Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) ** 2;
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  }

  function defaultRadiusMeters(country) {
    return country === 'US' ? 20 * 1609.34 : 30 * 1000;
  }

  function formatDistance(meters, country) {
    return country === 'US'
      ? `${(meters / 1609.34).toFixed(1)} mi`
      : `${(meters / 1000).toFixed(1)} km`;
  }

  /*
   * Measure every store, then filter by radius.
   *
   * Returns { results, locatable, skipped } rather than stashing a count on the
   * caller. "No stores within 20 miles" is only true if we could place stores
   * on a map at all; when `locatable` is 0 the caller must say something else,
   * or it tells a shopper the nearest Tandy is a long drive away when really
   * the data failed to load.
   */
  function nearby(userLoc, stores, options) {
    const opts = options || {};
    const located = [];
    const skipped = [];

    (stores || []).forEach(store => {
      const lat = Number(store.lat);
      const lon = Number(store.long);
      // A blank coordinate coerces to 0, which silently places the store off
      // the coast of Africa and drops it from every search. Skip and report
      // those rather than quietly measuring from the wrong place.
      if (!Number.isFinite(lat) || !Number.isFinite(lon) || (lat === 0 && lon === 0)) {
        skipped.push(store.location_name || store.name || '(unnamed store)');
        return;
      }
      located.push(Object.assign({}, store, {
        lat,
        long: lon,
        distance_m: haversine(userLoc.lat, userLoc.lng, lat, lon),
      }));
    });

    if (skipped.length) {
      console.warn(
        `store-search: ${skipped.length} of ${(stores || []).length} stores have no usable ` +
        'latitude/longitude and were excluded from search:',
        skipped
      );
    }

    located.sort((a, b) => a.distance_m - b.distance_m);

    const maxMeters = opts.maxMeters;
    let results = Number.isFinite(maxMeters)
      ? located.filter(s => s.distance_m <= maxMeters)
      : located;

    // A dedicated locator page would rather show the nearest few than an empty
    // page, so it can ask for a fallback. The header dropdown does not.
    if (!results.length && opts.fallbackNearest) {
      results = located.slice(0, opts.fallbackNearest);
    }
    if (opts.limit) results = results.slice(0, opts.limit);

    return { results, locatable: located.length, skipped };
  }

  /* ----------------------------------------------------------------------
   * Selected store.
   *
   * Three things read this key: the header dropdown, the PDP pickup panel
   * (snippets/store-pickup.liquid) and now the store pages and locator. The
   * stored shape { data, ts } and the key itself are a contract between them —
   * changing either silently breaks the pickup badge, which matches the saved
   * store against store_availabilities BY NAME. location_name must stay the
   * exact Shopify location name for that reason.
   * -------------------------------------------------------------------- */
  const SELECTED_KEY = 'ss_selected_store';
  const SELECTED_TTL_MS = 14 * 24 * 60 * 60 * 1000; // 14 days

  function getSelected() {
    try {
      const raw = localStorage.getItem(SELECTED_KEY);
      if (!raw) return null;
      const obj = JSON.parse(raw);
      if ((Date.now() - obj.ts) > SELECTED_TTL_MS) {
        localStorage.removeItem(SELECTED_KEY);
        return null;
      }
      return obj.data;
    } catch (err) { return null; }
  }

  /*
   * Does an entry from store_availabilities describe the same shop as an entry
   * from the store directory?
   *
   * This is the only place two independent sources have to agree about a
   * store's identity, and getting it wrong is invisible: a mismatch reads as
   * "Unavailable At This Location" on every product page, which looks like an
   * inventory fact rather than a bug.
   *
   * It compared display names, which is why tandy_stores.title had to stay
   * byte-identical to the Shopify location name. snippets/store-pickup-data.liquid
   * now also emits the location's custom.storeid metafield — the same join key
   * used everywhere else — so the comparison is on a stable id.
   *
   * The name comparison stays as a fallback, and is load-bearing in two real
   * cases: a location with no storeid metafield, and a store saved to
   * localStorage before this shipped. Falling back is the old behaviour
   * exactly, so this can only ever match more than before, never less.
   */
  function samePickupStore(pickup, store) {
    if (!pickup || !store) return false;
    const key = value => String(value == null ? '' : value).trim().toUpperCase();
    const pickupId = key(pickup.store_id);
    const storeId = key(store.store_id);
    if (pickupId && storeId) return pickupId === storeId;
    return Boolean(pickup.name) && pickup.name === store.location_name;
  }

  function isSelected(locationName) {
    const current = getSelected();
    return Boolean(current && locationName && current.location_name === locationName);
  }

  /* The cart attribute is what carries the choice through to checkout, so a
     failure here matters more than a failed UI update — it is awaited and
     reported rather than fired and forgotten. */
  async function updateCartAttributes(locationName) {
    if (!locationName) return null;
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    const response = await fetch(`${root}cart/update.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes: { location: `${locationName}` } }),
    });
    if (!response.ok) throw new Error(`cart/update.js returned ${response.status}`);
    return response.json();
  }

  /*
   * Whether choosing a store is also written to the cart (`attributes.location`).
   *
   * Only pickup needs that: it is what carries the store through to checkout
   * and onto the order. Until pickup is live a saved store is a browsing
   * preference — which store's hours and classes to show — and stamping every
   * order with it would put a store name on orders that ship from the
   * warehouse. So it is opt-in: the pickup (BOPIS) theme sets
   * `data-store-cart-sync="true"` on <html>; nothing else does.
   */
  function cartSyncEnabled() {
    return typeof document !== 'undefined'
      && document.documentElement.getAttribute('data-store-cart-sync') === 'true';
  }

  /* Forget the saved store. Announced like a selection, with no store. */
  function clearSelected() {
    try { localStorage.removeItem(SELECTED_KEY); } catch (err) { /* private mode */ }
    refreshSelectButtons();
    window.dispatchEvent(new CustomEvent('store:selected', { detail: { store: null, error: null } }));
  }

  async function selectStore(store, options) {
    if (!store || !store.location_name) return null;
    const opts = options || {};
    const data = Object.assign({}, store, {
      distance: opts.distance || store.distance || '',
      saved_at: Date.now(),
    });
    localStorage.setItem(SELECTED_KEY, JSON.stringify({ data, ts: Date.now() }));

    let error = null;
    if (cartSyncEnabled()) {
      try {
        await updateCartAttributes(data.location_name);
      } catch (err) {
        // Keep the local choice — the shopper still sees their store — but say
        // so, because checkout will not know about it.
        console.error('store-search: could not save the store to the cart', err);
        error = err;
      }
    }
    refreshSelectButtons();
    window.dispatchEvent(new CustomEvent('store:selected', { detail: { store: data, error } }));
    return data;
  }

  /* Buttons are matched by attribute and handled by one delegated listener, so
     markup rendered later by the locator needs no extra wiring. */
  function refreshSelectButtons(root) {
    const scope = root || document;
    scope.querySelectorAll('[data-store-select]').forEach(button => {
      let name = button.getAttribute('data-store-name');
      if (!name) {
        try { name = (JSON.parse(button.getAttribute('data-store-select')) || {}).location_name; }
        catch (err) { name = ''; }
      }
      const selected = isSelected(name);
      button.classList.toggle('is-selected', selected);
      button.setAttribute('aria-pressed', selected ? 'true' : 'false');
      const label = selected
        ? (button.getAttribute('data-label-selected') || 'Your store')
        : (button.getAttribute('data-label') || 'Make this my store');
      button.textContent = label;
    });
  }

  if (typeof document !== 'undefined') {
    document.addEventListener('click', event => {
      const button = event.target.closest && event.target.closest('[data-store-select]');
      if (!button) return;
      event.preventDefault();
      let store;
      try { store = JSON.parse(button.getAttribute('data-store-select')); }
      catch (err) { console.error('store-search: unreadable store payload', err); return; }
      button.disabled = true;
      selectStore(store, { distance: button.getAttribute('data-store-distance') || '' })
        .finally(() => { button.disabled = false; });
    });

    // The header dropdown can change the store on the same page, so buttons
    // re-read their state whenever anything announces a change.
    window.addEventListener('store:selected', () => refreshSelectButtons());
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => refreshSelectButtons());
    } else {
      refreshSelectButtons();
    }
  }

  /*
   * What a shopper sees for a store: "002-Denver, CO" -> "Denver, CO", with
   * "002" as the store number. Display only — `location_name` stays the exact
   * Location name everywhere it is stored or compared, because the pickup
   * badge matches on it. Same rule as snippets/store-label.liquid: only a
   * leading 1-4 digit run followed by a hyphen is a store number.
   */
  const LABEL_PATTERN = /^\s*(\d{1,4})\s*-\s*(.+?)\s*$/;

  function placeName(locationName) {
    const raw = String(locationName || '').trim();
    const match = LABEL_PATTERN.exec(raw);
    return match ? match[2] : raw;
  }

  function storeNumber(locationName) {
    const match = LABEL_PATTERN.exec(String(locationName || ''));
    return match ? match[1] : '';
  }

  /* 24h "10:00" from the metaobject -> "10 AM", "9:30 PM", as on the store
     pages (snippets/store-hours-time.liquid). */
  function clock(value) {
    const match = /^(\d{1,2}):(\d{2})/.exec(String(value || '').trim());
    if (!match) return String(value == null ? '' : value);
    const hour = Number(match[1]);
    const meridiem = hour >= 12 ? 'PM' : 'AM';
    const shown = hour % 12 === 0 ? 12 : hour % 12;
    return `${shown}${match[2] === '00' ? '' : ':' + match[2]} ${meridiem}`;
  }

  /* The reopen date, if the store is temporarily closed today (Yext's
     reopenDate, still ahead), as a Date; otherwise null. */
  function reopensOn(store) {
    const reopen = store && typeof store.reopen_date === 'string' ? store.reopen_date : '';
    if (!/^\d{4}-\d{2}-\d{2}$/.test(reopen)) return null;
    const now = new Date();
    const pad = n => String(n).padStart(2, '0');
    const today = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
    if (!(reopen > today)) return null;
    const [y, m, d] = reopen.split('-').map(Number);
    return new Date(y, m - 1, d);
  }

  /*
   * One line about today for a store: "Today: 10 AM–6 PM", "Closed today", or
   * "Temporarily closed · reopens Oct 16". Empty when the store has no hours.
   * Plain text, built only from times and fixed words. Shared by the locator
   * cards and the header store selector so the two cannot disagree.
   */
  function todayText(store) {
    if (!store) return '';
    const reopen = reopensOn(store);
    if (reopen) {
      const when = reopen.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      return `Temporarily closed · reopens ${when}`;
    }
    const hours = store.hours;
    if (!hours || typeof hours !== 'object') return '';
    const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
    const intervals = hours[days[(new Date().getDay() + 6) % 7]];
    if (!Array.isArray(intervals)) return '';
    if (!intervals.length) return 'Closed today';
    return 'Today: ' + intervals.map(i => `${clock(i.open)}–${clock(i.close)}`).join(', ');
  }

  return {
    placeName,
    storeNumber,
    clock,
    todayText,
    clearSelected,
    cartSyncEnabled,
    loadPostal,
    loadDirectory,
    lookupPostal,
    nearby,
    haversine,
    formatDistance,
    defaultRadiusMeters,
    getSelected,
    isSelected,
    samePickupStore,
    selectStore,
    updateCartAttributes,
    refreshSelectButtons,
    SELECTED_KEY,
  };
})();

